/**
 * library.ts — the built-in vector picture gallery.
 *
 * Each picture defines a palette (paint-by-number colors) and a set of REGIONS
 * (polygons) per color. buildCells() shatters them into mosaic cells whose count
 * roughly matches the size profile in math/config.json.
 *
 * Pictures: Sunset Harbor, Curious Fox, Hot-Air Balloon, Sleepy Cat, Twin Peaks.
 */
import { VIEW, type PictureDef } from './types';
import { buildCells, ngon, rect, type Region } from './geometry';
import generatedPictures from './generated.json';
import { PICTURE_SPECS } from '../net/mathConfig';

const V = VIEW;

function spec(name: string) {
  return PICTURE_SPECS.find((s) => s.name === name) ?? PICTURE_SPECS[0];
}

/** Map a size spec's cells_per_color onto our palette colorIds, best-effort. */
function perColorTarget(
  sizeName: string,
  colorIds: number[],
): Record<number, number> {
  const s = spec(sizeName);
  const out: Record<number, number> = {};
  colorIds.forEach((cid, i) => {
    out[cid] = s.cells_per_color[i % s.cells_per_color.length];
  });
  return out;
}

// ---------------------------------------------------------------------------
// 1) SUNSET HARBOR  (medium, 8 colors)
// ---------------------------------------------------------------------------
function sunsetHarbor(): PictureDef {
  const palette = [
    { colorId: 1, number: 1, hex: '#ffd166' }, // sky upper gold
    { colorId: 2, number: 2, hex: '#ff8c5a' }, // sky lower coral
    { colorId: 3, number: 3, hex: '#ff5d73' }, // sun pink
    { colorId: 4, number: 4, hex: '#6a4c93' }, // far hills purple
    { colorId: 5, number: 5, hex: '#1b5a7a' }, // water deep teal
    { colorId: 6, number: 6, hex: '#2bc0a8' }, // water highlight
    { colorId: 7, number: 7, hex: '#3a2c52' }, // boats / silhouette
    { colorId: 8, number: 8, hex: '#ffe9c7' }, // sun glow
  ];
  const regions: Region[] = [
    // sky bands
    { colorId: 1, poly: rect(0, 0, V, 230) },
    { colorId: 2, poly: rect(0, 230, V, 200) },
    // sun glow + sun
    { colorId: 8, poly: ngon(V * 0.5, 470, 200, 26) },
    { colorId: 3, poly: ngon(V * 0.5, 470, 120, 26) },
    // far hills
    { colorId: 4, poly: [0, 470, 250, 410, 520, 470, 760, 415, V, 470, V, 540, 0, 540] },
    // water
    { colorId: 5, poly: rect(0, 540, V, 460) },
    { colorId: 6, poly: [V * 0.5 - 130, 560, V * 0.5 + 130, 560, V * 0.5 + 60, 1000, V * 0.5 - 60, 1000] },
    // boats
    { colorId: 7, poly: [260, 720, 420, 720, 380, 790, 300, 790] },
    { colorId: 7, poly: [600, 800, 770, 800, 730, 880, 640, 880] },
    { colorId: 7, poly: [332, 600, 348, 600, 348, 720, 332, 720] }, // mast
  ];
  const colorIds = palette.map((p) => p.colorId);
  return {
    id: 'sunset-harbor',
    title: 'Sunset Harbor',
    sizeName: 'medium',
    view: V,
    palette,
    cells: buildCells(regions, spec('medium').total_cells, 'sunset-harbor', perColorTarget('medium', colorIds)),
    cardTint: ['#ffd166', '#ff5d73'],
  };
}

// ---------------------------------------------------------------------------
// 2) CURIOUS FOX  (small, 4 colors)
// ---------------------------------------------------------------------------
function curiousFox(): PictureDef {
  const palette = [
    { colorId: 1, number: 1, hex: '#ff7a59' }, // fox orange
    { colorId: 2, number: 2, hex: '#fbeee0' }, // cream chest/face
    { colorId: 3, number: 3, hex: '#3a2c52' }, // nose/eyes/ears tips
    { colorId: 4, number: 4, hex: '#a7e8d6' }, // mint background
  ];
  const regions: Region[] = [
    { colorId: 4, poly: rect(0, 0, V, V) }, // bg
    // head
    { colorId: 1, poly: [300, 300, 700, 300, 760, 520, 620, 720, 380, 720, 240, 520] },
    // ears
    { colorId: 1, poly: [300, 300, 250, 120, 430, 250] },
    { colorId: 1, poly: [700, 300, 750, 120, 570, 250] },
    { colorId: 3, poly: [288, 200, 250, 120, 360, 220] }, // ear tip
    { colorId: 3, poly: [712, 200, 750, 120, 640, 220] },
    // face cream
    { colorId: 2, poly: [380, 430, 620, 430, 660, 560, 500, 700, 340, 560] },
    // nose
    { colorId: 3, poly: [470, 600, 530, 600, 500, 660] },
    // eyes
    { colorId: 3, poly: ngon(400, 470, 34, 16) },
    { colorId: 3, poly: ngon(600, 470, 34, 16) },
  ];
  const colorIds = palette.map((p) => p.colorId);
  return {
    id: 'curious-fox',
    title: 'Curious Fox',
    sizeName: 'small',
    view: V,
    palette,
    cells: buildCells(regions, spec('small').total_cells, 'curious-fox', perColorTarget('small', colorIds)),
    cardTint: ['#ff7a59', '#a7e8d6'],
  };
}

// ---------------------------------------------------------------------------
// 3) HOT-AIR BALLOON  (small, 4 colors)
// ---------------------------------------------------------------------------
function hotAirBalloon(): PictureDef {
  const palette = [
    { colorId: 1, number: 1, hex: '#8b5cf6' }, // balloon purple panels
    { colorId: 2, number: 2, hex: '#ff7a59' }, // balloon coral panels
    { colorId: 3, number: 3, hex: '#ffd166' }, // basket gold + sky glow
    { colorId: 4, number: 4, hex: '#bfe3ff' }, // sky blue
  ];
  const regions: Region[] = [
    { colorId: 4, poly: rect(0, 0, V, V) },
    // balloon envelope (big rounded shape) split into alternating panels
    { colorId: 1, poly: [500, 120, 360, 200, 320, 360, 420, 480, 500, 500] },
    { colorId: 2, poly: [500, 120, 640, 200, 680, 360, 580, 480, 500, 500] },
    { colorId: 1, poly: [500, 120, 500, 500, 580, 480, 560, 230] },
    { colorId: 2, poly: [500, 120, 500, 500, 420, 480, 440, 230] },
    { colorId: 1, poly: ngon(360, 300, 80, 18) },
    { colorId: 2, poly: ngon(640, 300, 80, 18) },
    // ropes
    { colorId: 3, poly: [430, 500, 450, 500, 470, 700, 455, 700] },
    { colorId: 3, poly: [570, 500, 550, 500, 530, 700, 545, 700] },
    // basket
    { colorId: 3, poly: [450, 700, 550, 700, 540, 800, 460, 800] },
    // little clouds
    { colorId: 3, poly: ngon(200, 760, 70, 16) },
    { colorId: 3, poly: ngon(820, 700, 60, 16) },
  ];
  const colorIds = palette.map((p) => p.colorId);
  return {
    id: 'hot-air-balloon',
    title: 'Drift Balloon',
    sizeName: 'small',
    view: V,
    palette,
    cells: buildCells(regions, spec('small').total_cells, 'hot-air-balloon', perColorTarget('small', colorIds)),
    cardTint: ['#8b5cf6', '#bfe3ff'],
  };
}

// ---------------------------------------------------------------------------
// 4) SLEEPY CAT  (medium, 8 colors)
// ---------------------------------------------------------------------------
function sleepyCat(): PictureDef {
  const palette = [
    { colorId: 1, number: 1, hex: '#f4ecfb' }, // wall
    { colorId: 2, number: 2, hex: '#e0c3fc' }, // wall shade
    { colorId: 3, number: 3, hex: '#6d6875' }, // cat grey body
    { colorId: 4, number: 4, hex: '#b5838d' }, // cat ears/nose pink
    { colorId: 5, number: 5, hex: '#3a2c52' }, // stripes / eyes
    { colorId: 6, number: 6, hex: '#ffd166' }, // window moon
    { colorId: 7, number: 7, hex: '#2bc0a8' }, // cushion teal
    { colorId: 8, number: 8, hex: '#8b5cf6' }, // cushion shade
  ];
  const regions: Region[] = [
    { colorId: 1, poly: rect(0, 0, V, 620) },
    { colorId: 2, poly: rect(0, 0, 360, 620) },
    // moon window
    { colorId: 6, poly: ngon(760, 220, 110, 24) },
    // cushion
    { colorId: 7, poly: rect(120, 620, 760, 280) },
    { colorId: 8, poly: rect(120, 820, 760, 80) },
    // cat body (curled)
    { colorId: 3, poly: [280, 560, 720, 560, 800, 700, 700, 820, 300, 820, 200, 700] },
    // ears
    { colorId: 4, poly: [320, 560, 300, 460, 400, 540] },
    { colorId: 4, poly: [680, 560, 700, 460, 600, 540] },
    // stripes
    { colorId: 5, poly: rect(380, 600, 30, 180) },
    { colorId: 5, poly: rect(480, 600, 30, 180) },
    { colorId: 5, poly: rect(580, 600, 30, 180) },
    // closed eyes + nose
    { colorId: 5, poly: rect(420, 640, 50, 12) },
    { colorId: 5, poly: rect(540, 640, 50, 12) },
    { colorId: 4, poly: ngon(500, 690, 20, 12) },
  ];
  const colorIds = palette.map((p) => p.colorId);
  return {
    id: 'sleepy-cat',
    title: 'Sleepy Cat',
    sizeName: 'medium',
    view: V,
    palette,
    cells: buildCells(regions, spec('medium').total_cells, 'sleepy-cat', perColorTarget('medium', colorIds)),
    cardTint: ['#e0c3fc', '#2bc0a8'],
  };
}

// ---------------------------------------------------------------------------
// 5) TWIN PEAKS  (large, 16 colors)  — cosmos sky + layered mountains
// ---------------------------------------------------------------------------
function twinPeaks(): PictureDef {
  const palette = [
    { colorId: 1, number: 1, hex: '#241046' },
    { colorId: 2, number: 2, hex: '#3b1d6e' },
    { colorId: 3, number: 3, hex: '#5b2a86' },
    { colorId: 4, number: 4, hex: '#8b5cf6' },
    { colorId: 5, number: 5, hex: '#c084fc' },
    { colorId: 6, number: 6, hex: '#ff7a59' },
    { colorId: 7, number: 7, hex: '#ffd166' },
    { colorId: 8, number: 8, hex: '#fff3d6' }, // stars/moon
    { colorId: 9, number: 9, hex: '#1b5a7a' },
    { colorId: 10, number: 10, hex: '#2bc0a8' },
    { colorId: 11, number: 11, hex: '#37d6b4' },
    { colorId: 12, number: 12, hex: '#0f3d52' },
    { colorId: 13, number: 13, hex: '#6a4c93' },
    { colorId: 14, number: 14, hex: '#b5838d' },
    { colorId: 15, number: 15, hex: '#f4ecfb' }, // snow caps
    { colorId: 16, number: 16, hex: '#9385ad' },
  ];
  const regions: Region[] = [
    // sky bands (cosmos gradient)
    { colorId: 1, poly: rect(0, 0, V, 140) },
    { colorId: 2, poly: rect(0, 140, V, 120) },
    { colorId: 3, poly: rect(0, 260, V, 120) },
    { colorId: 4, poly: rect(0, 380, V, 90) },
    { colorId: 5, poly: rect(0, 470, V, 70) },
    // moon + stars
    { colorId: 8, poly: ngon(760, 170, 80, 24) },
    { colorId: 8, poly: ngon(200, 120, 14, 8) },
    { colorId: 8, poly: ngon(420, 220, 12, 8) },
    { colorId: 8, poly: ngon(900, 300, 14, 8) },
    { colorId: 8, poly: ngon(120, 340, 10, 8) },
    { colorId: 16, poly: ngon(560, 90, 10, 8) },
    { colorId: 16, poly: ngon(680, 420, 12, 8) },
    // glow over peaks
    { colorId: 6, poly: [0, 540, V, 540, V, 600, 0, 600] },
    { colorId: 7, poly: [0, 600, V, 600, V, 640, 0, 640] },
    // far ridge
    { colorId: 13, poly: [0, 640, 250, 540, 500, 640, 750, 560, V, 660, V, 740, 0, 740] },
    // mid mountains
    { colorId: 9, poly: [0, 740, 280, 560, 560, 760, 0, 760] },
    { colorId: 12, poly: [440, 760, 720, 540, V, 760, V, 800, 440, 800] },
    // snow caps
    { colorId: 15, poly: [220, 600, 340, 600, 280, 560] },
    { colorId: 15, poly: [660, 600, 780, 600, 720, 540] },
    { colorId: 14, poly: [300, 660, 360, 660, 330, 600] },
    // foreground hills + lake
    { colorId: 10, poly: [0, 760, V, 800, V, 870, 0, 870] },
    { colorId: 11, poly: rect(0, 870, V, 130) },
  ];
  const colorIds = palette.map((p) => p.colorId);
  return {
    id: 'twin-peaks',
    title: 'Cosmos Peaks',
    sizeName: 'large',
    view: V,
    palette,
    cells: buildCells(regions, spec('large').total_cells, 'twin-peaks', perColorTarget('large', colorIds)),
    cardTint: ['#3b1d6e', '#37d6b4'],
  };
}

let _cache: PictureDef[] | null = null;

// Discovery taxonomy for the gallery category chips. Tags are intentionally a small flat set so
// the chip strip stays one line on mobile — when the catalog grows, group rarely-used tags under
// "More" (deferred).
const SEED_TAGS: Record<string, string[]> = {
  'sports-car': ['Luxury', 'Cars'],
  'casino-chips': ['Luxury', 'Casino'],
  'cocktail': ['Luxury', 'Drinks'],
  'neon-city': ['Luxury', 'City'],
  'yacht': ['Luxury'],
  'flamingo': ['Animals'],
  'red-fox': ['Animals'],
  'toucan': ['Animals'],
};

/**
 * All paintable pictures. Primary source is generated.json — REAL images run through the
 * art/ pipeline (quantise -> connected regions -> traced irregular polygons). The procedural
 * vector pictures remain as a fallback if the generated catalog is ever empty.
 */
export function getPictureLibrary(): PictureDef[] {
  if (!_cache) {
    const gen = generatedPictures as unknown as PictureDef[];
    _cache = gen && gen.length
      ? gen
      : [curiousFox(), hotAirBalloon(), sunsetHarbor(), sleepyCat(), twinPeaks()];
    // graft tags onto whichever source we're using
    for (const p of _cache) if (!p.tags) p.tags = SEED_TAGS[p.id] ?? [];
  }
  return _cache;
}

export function getPictureById(id: string): PictureDef | undefined {
  return getPictureLibrary().find((p) => p.id === id);
}
